import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { NavigationService } from 'src/app/core/api/navigation.service';
import { defaultSideNavSchema } from 'src/app/core/schemas/navigation-schema';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LoadingSpinnerService } from 'src/app/shared/loading-spinner/loading-spinner.service';
import { IntakeFormService } from '../intake-form.service';
import { IntakeForm } from './../../../API';

export enum IntakeFormSortOption {
  date = 'date'
}

@Component({
  selector: 'csi-intake-forms-view',
  templateUrl: './intake-forms-view.component.html',
  styleUrls: ['./intake-forms-view.component.scss']
})
export class IntakeFormsViewComponent implements OnInit, OnDestroy {
  constructor(
    public intakeFormService: IntakeFormService,
    private clinicSetupService: ClinicSetupService,
    private loadingSpinnerService: LoadingSpinnerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    public appStateService: AppStateService
  ) {
    this.refresh$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          this.isLoaded = false;
          return this.intakeFormService
            .getIntakeForms(this.clinicSetupService.clinicId, 'network-only')
            .pipe(finalize(() => this.loadingSpinnerService.hide()));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => (this.isLoaded = true),
        error: err => {
          console.error('Refresh failed:', err);
          this.isLoaded = false;
        }
      });
  }

  public isLoaded: boolean;
  public isLinked: boolean;
  public intakeFormList: IntakeForm[] = [];

  private refresh$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  public sortOptions: { [key in IntakeFormSortOption]: string } = {
    date: 'Date'
  };

  public filterByLinkedControl = new FormControl(false);
  public filterByUnlinkedControl = new FormControl(false);

  private readonly defaultSortOption = 'date';

  public searchControl = new FormControl('');
  public sortBySelectionControl = new FormControl(this.defaultSortOption);
  public filteredIntakeForms: IntakeForm[] = [];
  public isAsc = false;

  ngOnInit() {
    this.navigationService.updateSideNavSchema(defaultSideNavSchema);
    this.loadingSpinnerService.show();
    this.intakeFormService
      .getIntakeForms(this.clinicSetupService.clinicId)
      .pipe(untilDestroyed(this))

      .subscribe(() => {
        this.isLoaded = true;
        this.loadingSpinnerService.hide();
      });

    this.appStateService.intakeForms$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(intakeForms => {
        this.intakeFormList = [...intakeForms];
        this.filteredIntakeForms = [...intakeForms];
        this.searchControl.setValue('');
      });

    this.appStateService.currentIntakeForm$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(intakeForm => {
        if (!intakeForm) return;
        const listItem = this.filteredIntakeForms.find(f => f.id === intakeForm.id);
        if (listItem) {
          listItem.intakeFormPatientId = intakeForm.intakeFormPatientId;
        }
      });

    this.searchControl.valueChanges.pipe(debounceTime(300), untilDestroyed(this)).subscribe(() => {
      this.filterIntakeForms();
    });

    this.sortBySelectionControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(key => this.sort(key));

    this.filterByLinkedControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.filterIntakeForms());
    this.filterByUnlinkedControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.filterIntakeForms());
  }

  onIntakeFormSelect(intakeForm: IntakeForm) {
    this.router.navigate([intakeForm.id], {
      relativeTo: this.activatedRoute
    });
  }

  public toggleIsAsc() {
    this.isAsc = !this.isAsc;
    this.sort(this.sortBySelectionControl.value);
  }

  public sort(key: IntakeFormSortOption) {
    if (!this.sortOptions[key]) {
      return;
    }
    switch (key) {
      case IntakeFormSortOption.date:
        this.filteredIntakeForms.sort((a, b) => {
          return this.compare(
            new Date(a.createdAt).getTime(),
            new Date(b.createdAt).getTime(),
            this.isAsc
          );
        });
        break;
    }
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  refresh(): void {
    if (!this.isLoaded) {
      return;
    }
    this.clearSearch();
    this.clearFilters();
    this.refresh$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  compareByUnlinked(a: IntakeForm, b: IntakeForm) {
    const aScore = !a.intakeFormPatientId ? 2 : 0;
    const bScore = !b.intakeFormPatientId ? 2 : 0;
    if (aScore !== bScore) {
      return (aScore - bScore) * (this.isAsc ? 1 : -1);
    }

    return this.compare(
      new Date(a.createdAt).getTime(),
      new Date(b.createdAt).getTime(),
      this.isAsc
    );
  }
  filterIntakeForms() {
    const lowerTerm = this.searchControl.value.toLowerCase().trim();

    this.filteredIntakeForms = [
      ...this.intakeFormList.filter(form => {
        const response = safeJsonParse(form.response);
        if (!response) return false;

        let patientInfo = null;

        if (response && response.answers && response.answers.patientInformation) {
          patientInfo = response.answers.patientInformation;
        } else if (response && response.patientInformation) {
          patientInfo = response.patientInformation;
        }

        if (!patientInfo) return false;

        const firstName = (patientInfo.firstName || '').toString().toLowerCase();
        const lastName = (patientInfo.lastName || '').toString().toLowerCase();

        return (firstName + ' ' + lastName).includes(lowerTerm);
      })
    ];

    const filterByLinked = this.filterByLinkedControl.value;
    const filterByUnlinked = this.filterByUnlinkedControl.value;
    if (filterByLinked && !filterByUnlinked) {
      this.filteredIntakeForms = [
        ...this.filteredIntakeForms.filter(intakeForm => intakeForm.intakeFormPatientId)
      ];
    } else if (filterByUnlinked && !filterByLinked) {
      this.filteredIntakeForms = [
        ...this.filteredIntakeForms.filter(intakeForm => !intakeForm.intakeFormPatientId)
      ];
    }
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  clearFilters() {
    this.filterByLinkedControl.patchValue(false);
    this.filterByUnlinkedControl.patchValue(false);
  }

  noSort() {
    return 0;
  }
}
